import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ContactPickItem } from '@tww/shared';
import { fmtEur, ratingWarning } from '@tww/shared';
import { api, qs } from '../api/client';
import { Field } from './fields';
import { RatingBadge } from './basics';
import { Icon } from './Icon';
import { ContactQuickCreate } from '../pages/contacts/ContactQuickCreate';

/** ContactPicker: Suche in einer Kontaktkategorie, Anzeige mit Bewertung und Ort, Neuanlage im Popup */
export function ContactPicker({
  value,
  onChange,
  category,
  region,
  label,
  hint,
  error,
  required,
  allowCreate = true,
  placeholder = 'Kontakt suchen …',
  freeText,
}: {
  value: ContactPickItem | null;
  onChange: (c: ContactPickItem | null) => void;
  category?: string;
  region?: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  allowCreate?: boolean;
  placeholder?: string;
  /** Eingabe ohne Kontakt übernehmen (z. B. externe Person), als letzter Eintrag der Liste */
  freeText?: { label: (q: string) => string; onPick: (q: string) => void };
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const [create, setCreate] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const list = useQuery({
    queryKey: ['contacts-pick', category, region, q],
    queryFn: () => api.get<{ items: ContactPickItem[] }>(`/contacts/pick${qs({ category, region, q, limit: 20 })}`).then((r) => r.items),
    enabled: open,
    staleTime: 10_000,
  });
  const items = list.data ?? [];
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const pickFree = () => {
    freeText?.onPick(q.trim());
    setQ('');
    setOpen(false);
  };
  const warn = useMemo(() => (value ? ratingWarning(value.rating_grade, value.rating_reason) : null), [value]);

  return (
    <Field label={label} hint={warn ? <span className="miss">{warn}</span> : hint} error={error} required={required}>
      <div className="picker" ref={ref}>
        {value ? (
          <div className="chosen">
            <RatingBadge grade={value.rating_grade} reason={value.rating_reason} />
            <span className="grow">
              {value.display_name}
              {value.sub && <span className="mut small"> · {value.sub}</span>}
            </span>
            <button type="button" className="icon-btn" aria-label="Auswahl entfernen" onClick={() => onChange(null)}>
              <Icon name="x" />
            </button>
          </div>
        ) : (
          <input
            value={q}
            placeholder={placeholder}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
              setSel(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') setSel((s) => Math.min(items.length - 1, s + 1));
              else if (e.key === 'ArrowUp') setSel((s) => Math.max(0, s - 1));
              else if (e.key === 'Enter' && items[sel]) {
                e.preventDefault();
                onChange(items[sel]!);
                setOpen(false);
                setQ('');
              } else if (e.key === 'Enter' && freeText && q.trim() && !items.length) {
                e.preventDefault();
                pickFree();
              } else if (e.key === 'Escape') setOpen(false);
            }}
            aria-autocomplete="list"
            aria-expanded={open}
          />
        )}
        {open && !value && (
          <div className="pick-list" role="listbox">
            {list.isLoading && <div className="pick-item mut">Suche …</div>}
            {!list.isLoading && items.length === 0 && <div className="pick-item mut">Kein Kontakt gefunden</div>}
            {items.map((c, i) => (
              <div
                key={c.id}
                role="option"
                aria-selected={i === sel}
                className={`pick-item ${i === sel ? 'sel' : ''}`}
                onMouseEnter={() => setSel(i)}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setQ('');
                }}
              >
                <RatingBadge grade={c.rating_grade} reason={c.rating_reason} />
                <span className="grow">
                  {c.display_name}
                  <span className="sub">
                    {[c.sub, c.region, c.last_price ? `${fmtEur(c.last_price.price)} (${c.last_price.year})` : null].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </div>
            ))}
            {freeText && q.trim() && (
              <div className="pick-new" onClick={pickFree}>
                <Icon name="plus" /> {freeText.label(q.trim())}
              </div>
            )}
            {allowCreate && (
              <div className="pick-new" onClick={() => setCreate(true)}>
                <Icon name="plus" /> Neuen Kontakt anlegen{q ? ` „${q}“` : ''}
              </div>
            )}
          </div>
        )}
      </div>
      {create && (
        <ContactQuickCreate
          initialName={q}
          category={category}
          region={region}
          onClose={() => setCreate(false)}
          onCreated={(c) => {
            onChange(c);
            setCreate(false);
            setOpen(false);
          }}
        />
      )}
    </Field>
  );
}
